/**
 * ScheduleValidationUtils.js
 * 
 * Pure validation functions for schedule and shift data.
 * This module handles all business rule validations, overlap detection,
 * and data integrity checks for the schedule system.
 * 
 * Dependencies: TimeSlotUtils (for time calculations and constraints)
 * 
 * @module ScheduleValidationUtils
 */

import {
    TIME_SLOT_MINUTES,
    SHIFT_CONSTRAINTS,
    parseTimeString,
    isSlotWithinBusinessHours
} from './TimeSlotUtils';

// ==================== VALIDATION ERROR MESSAGES ====================

/**
 * Predefined validation error messages
 */
export const VALIDATION_ERRORS = {
    // Required field errors
    STAFF_ID_REQUIRED: 'Staff ID is required',
    DATE_REQUIRED: 'Date is required',
    START_TIME_REQUIRED: 'Start time is required',
    END_TIME_REQUIRED: 'End time is required',
    TIMES_REQUIRED: 'Start and end times are required',
    
    // Time validation errors
    END_BEFORE_START: 'End time must be after start time',
    SAME_START_END: 'Start and end times cannot be the same',
    MIN_DURATION: `Minimum shift duration is ${SHIFT_CONSTRAINTS.MIN_DURATION} minutes`,
    MAX_DURATION: `Maximum shift duration is ${SHIFT_CONSTRAINTS.MAX_DURATION / 60} hours`,
    
    // Business hours errors
    START_OUTSIDE_HOURS: 'Start time is outside business hours',
    END_OUTSIDE_HOURS: 'End time is outside business hours',
    TIMES_OUTSIDE_HOURS: 'Shift times must be within business hours',
    
    // Date validation errors
    INVALID_DATE_FORMAT: 'Invalid date format (expected YYYY-MM-DD)',
    DATE_IN_PAST: 'Cannot create shifts in the past',
    
    // Overlap errors
    SHIFT_OVERLAP: 'Shift overlaps with existing shift(s)',
    BREAK_OVERLAP: 'Break overlaps with existing break',
    
    // Data integrity errors
    INVALID_SHIFT_DATA: 'Invalid shift data provided',
    MISSING_SHIFT_ID: 'Shift ID is required for update operations',
    SHIFT_NOT_FOUND: 'Shift not found',
    
    // Department/Staff errors
    INVALID_DEPARTMENT: 'Invalid department ID',
    STAFF_NOT_IN_DEPARTMENT: 'Staff member not assigned to this department',
    
    // Template errors
    TEMPLATE_NOT_FOUND: 'Schedule template not found',
    INVALID_TEMPLATE_DATA: 'Invalid template data',
    
    // Bulk operation errors
    EMPTY_BATCH: 'Cannot process empty batch of shifts',
    BATCH_VALIDATION_FAILED: 'One or more shifts in batch failed validation'
};

/**
 * Validation warning messages (non-critical)
 */
export const VALIDATION_WARNINGS = {
    LONG_SHIFT: `Shift exceeds ${SHIFT_CONSTRAINTS.BREAK_THRESHOLD / 60} hours - break may be required`,
    CONSECUTIVE_SHIFTS: 'Staff has consecutive shifts with insufficient rest period',
    OVERTIME_APPROACHING: 'Weekly hours approaching overtime threshold',
    WEEKEND_SHIFT: 'Shift scheduled on weekend',
    HOLIDAY_SHIFT: 'Shift scheduled on public holiday'
};

// ==================== DATE VALIDATION ====================

/**
 * Validate date format (YYYY-MM-DD)
 * 
 * @param {string} dateString - Date string to validate
 * @returns {{isValid: boolean, error: string|null}} Validation result
 * 
 * @example
 * validateDateFormat("2024-01-15") // { isValid: true, error: null }
 * validateDateFormat("15/01/2024") // { isValid: false, error: "Invalid date format..." }
 */
export const validateDateFormat = (dateString) => {
    if (!dateString || typeof dateString !== 'string') {
        return {
            isValid: false,
            error: VALIDATION_ERRORS.DATE_REQUIRED
        };
    }
    
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) {
        return {
            isValid: false,
            error: VALIDATION_ERRORS.INVALID_DATE_FORMAT
        };
    }
    
    // Validate actual date values
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        return {
            isValid: false,
            error: VALIDATION_ERRORS.INVALID_DATE_FORMAT
        };
    }
    
    // Check if date string matches the parsed date (catches invalid dates like 2024-02-30)
    const [year, month, day] = dateString.split('-').map(Number);
    if (date.getFullYear() !== year || 
        date.getMonth() !== month - 1 || 
        date.getDate() !== day) {
        return {
            isValid: false,
            error: VALIDATION_ERRORS.INVALID_DATE_FORMAT
        };
    }
    
    return { isValid: true, error: null };
};

/**
 * Check if date is in the past
 * 
 * @param {string} dateString - Date string (YYYY-MM-DD)
 * @param {boolean} [allowToday=true] - Whether today's date is valid
 * @returns {boolean} True if date is in the past
 */
export const isDateInPast = (dateString, allowToday = true) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (allowToday) {
        return date < today;
    } else {
        return date <= today;
    }
};

// ==================== TIME VALIDATION ====================

/**
 * Validate time format (HH:MM)
 * 
 * @param {string} timeString - Time string to validate
 * @returns {{isValid: boolean, error: string|null}} Validation result
 */
export const validateTimeFormat = (timeString) => {
    if (!timeString || typeof timeString !== 'string') {
        return {
            isValid: false,
            error: 'Time is required'
        };
    }
    
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(timeString)) {
        return {
            isValid: false,
            error: 'Invalid time format (expected HH:MM)'
        };
    }
    
    return { isValid: true, error: null };
};

/**
 * Validate that time is within business hours
 * 
 * @param {string} timeString - Time string (HH:MM)
 * @returns {{isValid: boolean, error: string|null}} Validation result
 */
export const validateBusinessHours = (timeString) => {
    const timeFormat = validateTimeFormat(timeString);
    if (!timeFormat.isValid) {
        return timeFormat;
    }
    
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (!isSlotWithinBusinessHours(hours, minutes)) {
        return {
            isValid: false,
            error: VALIDATION_ERRORS.TIMES_OUTSIDE_HOURS
        };
    }
    
    return { isValid: true, error: null };
};

/**
 * Validate shift duration
 * 
 * @param {number} durationMinutes - Duration in minutes
 * @returns {{isValid: boolean, error: string|null, warning: string|null}} Validation result
 */
export const validateShiftDuration = (durationMinutes) => {
    if (durationMinutes < SHIFT_CONSTRAINTS.MIN_DURATION) {
        return {
            isValid: false,
            error: VALIDATION_ERRORS.MIN_DURATION,
            warning: null
        };
    }
    
    if (durationMinutes > SHIFT_CONSTRAINTS.MAX_DURATION) {
        return {
            isValid: false,
            error: VALIDATION_ERRORS.MAX_DURATION,
            warning: null
        };
    }
    
    // Warning for long shifts that may require breaks
    let warning = null;
    if (durationMinutes > SHIFT_CONSTRAINTS.BREAK_THRESHOLD) {
        warning = VALIDATION_WARNINGS.LONG_SHIFT;
    }
    
    return { isValid: true, error: null, warning };
};

/**
 * Calculate duration between start and end times
 * 
 * @param {string|Date} startTime - Start time (Date object or ISO string)
 * @param {string|Date} endTime - End time (Date object or ISO string)
 * @returns {number} Duration in minutes
 */
export const calculateShiftDuration = (startTime, endTime) => {
    const start = startTime instanceof Date ? startTime : new Date(startTime);
    const end = endTime instanceof Date ? endTime : new Date(endTime);
    
    return (end - start) / (1000 * 60);
};

// ==================== SHIFT DATA VALIDATION ====================

/**
 * Validate complete shift data object
 * 
 * @param {Object} shiftData - Shift data to validate
 * @param {string} shiftData.staffId - Staff member ID
 * @param {string} shiftData.date - Shift date (YYYY-MM-DD)
 * @param {string|Date} shiftData.startTime - Shift start time
 * @param {string|Date} shiftData.endTime - Shift end time
 * @param {string} [shiftData.departmentId] - Department ID (optional)
 * @returns {{isValid: boolean, errors: string[], warnings: string[]}} Validation result
 * 
 * @example
 * validateShift({
 *   staffId: "staff123",
 *   date: "2024-01-15",
 *   startTime: "2024-01-15T09:00:00Z",
 *   endTime: "2024-01-15T17:00:00Z"
 * })
 * // Returns { isValid: true, errors: [], warnings: [] }
 */
export const validateShift = (shiftData) => {
    const errors = [];
    const warnings = [];
    
    // Validate required fields
    if (!shiftData || typeof shiftData !== 'object') {
        return {
            isValid: false,
            errors: [VALIDATION_ERRORS.INVALID_SHIFT_DATA],
            warnings: []
        };
    }
    
    if (!shiftData.staffId) {
        errors.push(VALIDATION_ERRORS.STAFF_ID_REQUIRED);
    }
    
    if (!shiftData.date) {
        errors.push(VALIDATION_ERRORS.DATE_REQUIRED);
    } else {
        // Validate date format
        const dateValidation = validateDateFormat(shiftData.date);
        if (!dateValidation.isValid) {
            errors.push(dateValidation.error);
        }
    }
    
    if (!shiftData.startTime || !shiftData.endTime) {
        errors.push(VALIDATION_ERRORS.TIMES_REQUIRED);
        return { isValid: false, errors, warnings };
    }
    
    // Convert times to Date objects for comparison
    const start = shiftData.startTime instanceof Date 
        ? shiftData.startTime 
        : new Date(shiftData.startTime);
    const end = shiftData.endTime instanceof Date 
        ? shiftData.endTime 
        : new Date(shiftData.endTime);
    
    // Validate time objects
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        errors.push('Invalid time format');
        return { isValid: false, errors, warnings };
    }
    
    // Validate time relationship
    if (start >= end) {
        if (start.getTime() === end.getTime()) {
            errors.push(VALIDATION_ERRORS.SAME_START_END);
        } else {
            errors.push(VALIDATION_ERRORS.END_BEFORE_START);
        }
    }
    
    // Validate duration
    if (start < end) {
        const durationMinutes = calculateShiftDuration(start, end);
        const durationValidation = validateShiftDuration(durationMinutes);
        
        if (!durationValidation.isValid) {
            errors.push(durationValidation.error);
        }
        
        if (durationValidation.warning) {
            warnings.push(durationValidation.warning);
        }
    }
    
    // Validate business hours (if time strings are available)
    const startHour = start.getHours();
    const startMinute = start.getMinutes();
    const endHour = end.getHours();
    const endMinute = end.getMinutes();
    
    if (!isSlotWithinBusinessHours(startHour, startMinute)) {
        errors.push(VALIDATION_ERRORS.START_OUTSIDE_HOURS);
    }
    
    // For end time, we need to check if it's exactly at END_HOUR+1:00 (which is valid as an end time)
    // or within business hours
    const isValidEndTime = (endHour === 24 && endMinute === 0) || 
                          isSlotWithinBusinessHours(endHour, endMinute) ||
                          (endHour === 23 && endMinute > 0); // Allow times between 23:00 and 24:00
    
    if (!isValidEndTime) {
        errors.push(VALIDATION_ERRORS.END_OUTSIDE_HOURS);
    }
    
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
};

/**
 * Validate shift update data
 * 
 * @param {string} shiftId - Shift ID being updated
 * @param {Object} updates - Update data
 * @param {Object} originalShift - Original shift data
 * @returns {{isValid: boolean, errors: string[], warnings: string[]}} Validation result
 */
export const validateShiftUpdate = (shiftId, updates, originalShift) => {
    if (!shiftId) {
        return {
            isValid: false,
            errors: [VALIDATION_ERRORS.MISSING_SHIFT_ID],
            warnings: []
        };
    }
    
    if (!originalShift) {
        return {
            isValid: false,
            errors: [VALIDATION_ERRORS.SHIFT_NOT_FOUND],
            warnings: []
        };
    }
    
    // Merge updates with original shift
    const updatedShift = {
        ...originalShift,
        ...updates,
        id: shiftId
    };
    
    // Validate the merged shift
    return validateShift(updatedShift);
};

// ==================== OVERLAP DETECTION ====================

/**
 * Check if two time ranges overlap
 * 
 * @param {Date} start1 - Start of first range
 * @param {Date} end1 - End of first range
 * @param {Date} start2 - Start of second range
 * @param {Date} end2 - End of second range
 * @returns {boolean} True if ranges overlap
 */
export const doTimeRangesOverlap = (start1, end1, start2, end2) => {
    return (
        (start1 >= start2 && start1 < end2) ||  // start1 within range2
        (end1 > start2 && end1 <= end2) ||      // end1 within range2
        (start1 <= start2 && end1 >= end2)      // range1 encompasses range2
    );
};

/**
 * Check for shift overlaps with existing shifts
 * 
 * @param {Object} newShift - New shift to check
 * @param {Array<Object>} existingShifts - Array of existing shifts
 * @param {string} [excludeShiftId=null] - Shift ID to exclude from check (for updates)
 * @returns {{hasOverlap: boolean, overlappingShifts: Array<Object>}} Overlap result
 * 
 * @example
 * const overlap = checkShiftOverlap(
 *   { date: "2024-01-15", startTime: "09:00", endTime: "17:00" },
 *   existingShifts
 * );
 * if (overlap.hasOverlap) {
 *   console.log(`Overlaps with ${overlap.overlappingShifts.length} shift(s)`);
 * }
 */
export const checkShiftOverlap = (newShift, existingShifts, excludeShiftId = null) => {
    if (!newShift || !existingShifts || !Array.isArray(existingShifts)) {
        return { hasOverlap: false, overlappingShifts: [] };
    }
    
    // Convert new shift times to Date objects
    const newStart = newShift.startTime instanceof Date 
        ? newShift.startTime 
        : new Date(newShift.startTime);
    const newEnd = newShift.endTime instanceof Date 
        ? newShift.endTime 
        : new Date(newShift.endTime);
    const newDate = newShift.date;
    
    // Validate new shift times
    if (isNaN(newStart.getTime()) || isNaN(newEnd.getTime())) {
        return { hasOverlap: false, overlappingShifts: [] };
    }
    
    // Filter and check for overlaps
    const overlapping = existingShifts.filter(shift => {
        // Skip if this is the shift being updated
        if (excludeShiftId && shift.id === excludeShiftId) {
            return false;
        }
        
        // Only check shifts on the same date
        if (shift.date !== newDate) {
            return false;
        }
        
        // Convert existing shift times to Date objects
        const existingStart = shift.startTime?.toDate 
            ? shift.startTime.toDate() 
            : new Date(shift.startTime);
        const existingEnd = shift.endTime?.toDate 
            ? shift.endTime.toDate() 
            : new Date(shift.endTime);
        
        // Validate existing shift times
        if (isNaN(existingStart.getTime()) || isNaN(existingEnd.getTime())) {
            return false;
        }
        
        // Check for overlap
        return doTimeRangesOverlap(newStart, newEnd, existingStart, existingEnd);
    });
    
    return {
        hasOverlap: overlapping.length > 0,
        overlappingShifts: overlapping
    };
};

/**
 * Format overlapping shifts for error message
 * 
 * @param {Array<Object>} overlappingShifts - Array of overlapping shifts
 * @returns {string} Formatted error message
 */
export const formatOverlapError = (overlappingShifts) => {
    if (!overlappingShifts || overlappingShifts.length === 0) {
        return '';
    }
    
    const timeRanges = overlappingShifts.map(shift => {
        const start = shift.startTime?.toDate 
            ? shift.startTime.toDate() 
            : new Date(shift.startTime);
        const end = shift.endTime?.toDate 
            ? shift.endTime.toDate() 
            : new Date(shift.endTime);
        
        const startTime = start.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        });
        const endTime = end.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        });
        
        return `${startTime}-${endTime}`;
    });
    
    return `${VALIDATION_ERRORS.SHIFT_OVERLAP}: ${timeRanges.join(', ')}`;
};

/**
 * Check for multiple shift overlaps in a batch
 * 
 * @param {Array<Object>} shifts - Array of shifts to check
 * @returns {{hasOverlaps: boolean, conflicts: Array<{shift: Object, overlaps: Array<Object>}>}} Conflict result
 */
export const checkBatchOverlaps = (shifts) => {
    const conflicts = [];
    
    for (let i = 0; i < shifts.length; i++) {
        const currentShift = shifts[i];
        const otherShifts = shifts.filter((_, index) => index !== i);
        
        const overlapCheck = checkShiftOverlap(currentShift, otherShifts);
        
        if (overlapCheck.hasOverlap) {
            conflicts.push({
                shift: currentShift,
                overlaps: overlapCheck.overlappingShifts
            });
        }
    }
    
    return {
        hasOverlaps: conflicts.length > 0,
        conflicts
    };
};

// ==================== SCHEDULE VALIDATION ====================

/**
 * Validate weekly hours don't exceed maximum
 * 
 * @param {number} totalHours - Total weekly hours
 * @param {number} [maxHours=60] - Maximum allowed weekly hours
 * @returns {{isValid: boolean, error: string|null, warning: string|null}} Validation result
 */
export const validateWeeklyHours = (totalHours, maxHours = 60) => {
    if (totalHours > maxHours) {
        return {
            isValid: false,
            error: `Weekly hours (${totalHours}) exceed maximum allowed (${maxHours})`,
            warning: null
        };
    }
    
    // Warning at 90% of max
    if (totalHours > maxHours * 0.9) {
        return {
            isValid: true,
            error: null,
            warning: VALIDATION_WARNINGS.OVERTIME_APPROACHING
        };
    }
    
    return { isValid: true, error: null, warning: null };
};

/**
 * Validate minimum rest period between consecutive shifts
 * 
 * @param {Object} shift1 - First shift
 * @param {Object} shift2 - Second shift
 * @param {number} [minRestMinutes=480] - Minimum rest period in minutes (default: 8 hours)
 * @returns {{isValid: boolean, error: string|null, warning: string|null}} Validation result
 */
export const validateRestPeriod = (shift1, shift2, minRestMinutes = 480) => {
    const end1 = shift1.endTime instanceof Date ? shift1.endTime : new Date(shift1.endTime);
    const start2 = shift2.startTime instanceof Date ? shift2.startTime : new Date(shift2.startTime);
    
    const restMinutes = (start2 - end1) / (1000 * 60);
    
    if (restMinutes < minRestMinutes) {
        const restHours = Math.floor(restMinutes / 60);
        const requiredHours = Math.floor(minRestMinutes / 60);
        
        return {
            isValid: false,
            error: `Insufficient rest period (${restHours}h) between shifts (minimum: ${requiredHours}h)`,
            warning: null
        };
    }
    
    return { isValid: true, error: null, warning: null };
};

/**
 * Validate shifts are in chronological order
 * 
 * @param {Array<Object>} shifts - Array of shifts
 * @returns {{isValid: boolean, errors: string[]}} Validation result
 */
export const validateShiftOrder = (shifts) => {
    const errors = [];
    
    for (let i = 1; i < shifts.length; i++) {
        const prev = shifts[i - 1];
        const current = shifts[i];
        
        const prevEnd = prev.endTime instanceof Date ? prev.endTime : new Date(prev.endTime);
        const currentStart = current.startTime instanceof Date ? current.startTime : new Date(current.startTime);
        
        if (currentStart < prevEnd) {
            errors.push(`Shift ${i + 1} starts before shift ${i} ends`);
        }
    }
    
    return {
        isValid: errors.length === 0,
        errors
    };
};

// ==================== BATCH VALIDATION ====================

/**
 * Validate batch of shifts
 * 
 * @param {Array<Object>} shifts - Array of shifts to validate
 * @returns {{isValid: boolean, results: Array<Object>, summary: Object}} Validation results
 * 
 * @example
 * const result = validateShiftBatch(shifts);
 * console.log(`Valid: ${result.summary.valid}, Invalid: ${result.summary.invalid}`);
 */
export const validateShiftBatch = (shifts) => {
    if (!shifts || !Array.isArray(shifts) || shifts.length === 0) {
        return {
            isValid: false,
            results: [],
            summary: {
                total: 0,
                valid: 0,
                invalid: 0,
                warnings: 0
            },
            error: VALIDATION_ERRORS.EMPTY_BATCH
        };
    }
    
    const results = shifts.map((shift, index) => {
        const validation = validateShift(shift);
        return {
            index,
            shift,
            isValid: validation.isValid,
            errors: validation.errors,
            warnings: validation.warnings
        };
    });
    
    const summary = {
        total: shifts.length,
        valid: results.filter(r => r.isValid).length,
        invalid: results.filter(r => !r.isValid).length,
        warnings: results.reduce((sum, r) => sum + r.warnings.length, 0)
    };
    
    // Check for internal overlaps
    const overlapCheck = checkBatchOverlaps(shifts.filter((_, i) => results[i].isValid));
    
    return {
        isValid: summary.invalid === 0 && !overlapCheck.hasOverlaps,
        results,
        summary,
        overlaps: overlapCheck.conflicts
    };
};

// ==================== TEMPLATE VALIDATION ====================

/**
 * Validate schedule template data
 * 
 * @param {Object} template - Template data to validate
 * @returns {{isValid: boolean, errors: string[]}} Validation result
 */
export const validateTemplate = (template) => {
    const errors = [];
    
    if (!template || typeof template !== 'object') {
        return {
            isValid: false,
            errors: [VALIDATION_ERRORS.INVALID_TEMPLATE_DATA]
        };
    }
    
    if (!template.name || typeof template.name !== 'string') {
        errors.push('Template name is required');
    }
    
    if (!template.shifts || !Array.isArray(template.shifts)) {
        errors.push('Template must include shifts array');
    } else if (template.shifts.length === 0) {
        errors.push('Template must include at least one shift');
    }
    
    return {
        isValid: errors.length === 0,
        errors
    };
};

// ==================== EXPORT ALL UTILITIES ====================

/**
 * Export all validation utilities as a single object
 */
export const ScheduleValidationUtils = {
    // Error messages
    VALIDATION_ERRORS,
    VALIDATION_WARNINGS,
    
    // Date validation
    validateDateFormat,
    isDateInPast,
    
    // Time validation
    validateTimeFormat,
    validateBusinessHours,
    validateShiftDuration,
    calculateShiftDuration,
    
    // Shift validation
    validateShift,
    validateShiftUpdate,
    
    // Overlap detection
    doTimeRangesOverlap,
    checkShiftOverlap,
    formatOverlapError,
    checkBatchOverlaps,
    
    // Schedule validation
    validateWeeklyHours,
    validateRestPeriod,
    validateShiftOrder,
    
    // Batch validation
    validateShiftBatch,
    
    // Template validation
    validateTemplate
};

export default ScheduleValidationUtils;